<script setup lang="ts">
// destructured imports
import { useSiteStore } from '~/stores/site.store';
import { useAuthStore } from '~/stores/auth.store';
import { unlockBodyScroll } from '~/utilities/ui-utilities';
import { useMagicKeys } from '@vueuse/core';
import type {
  IHamburgerMenuState,
  LayoutActiveStates,
} from '~/interfaces/components/layout';
// components
import NavigationBar from '~/components/user-interface/navigation-bar.vue';
import ErrorBoundary from '~/components/user-interface/error-boundary.vue';
import LinearGradients from '../design-system/linear-gradients.vue';
import useModalContext from '~/composables/modals/useModalContext';
import { actionHandlersConfig } from '~/utilities/query-actions';

// route
const router = useRouter();
const route = useRoute();
// stores
const settings = useSiteStore();
const auth = useAuthStore();
// destructured composables
const { $jackpotService } = useServices();
const { $t, $enabled, $toastService } = useNuxtApp();
const { ctrl_k } = useMagicKeys();
// composables
const runtimeConfig = useRuntimeConfig();
const viewport = useViewport();

useGetPage();
// refs
const offline = ref<boolean>(false);
const activeStates = reactive<LayoutActiveStates>({
  providers: false,
  topNavigation: false,
});

provide<LayoutActiveStates>('activeStates', activeStates);

onMounted(async () => {
  unlockBodyScroll();
  if (navigator.userAgent.indexOf('Opera Mini') !== -1) {
    await router.push('/browser-error.html');
  }
  addEventListener('offline', () => (offline.value = true));
  addEventListener('online', () => {
    offline.value = false;
    settings.deactivateModal();
    settings.toggleSiteNotice({
      heading: $t('connection-restored-heading'),
      content: {
        data: $t('connection-restored-message'),
      },
      severity: 'success',
      enabled: true,
      timeout: 5000,
    });
  });
});
// methods
const inGameMobile = computed(() => {
  return (
    !viewport.isGreaterThan('tablet') &&
    !!route.params?.game &&
    route.params.game !== 'lucky-numbers'
    // TODO: Lucky numbers
  );
});
const modalContent = computed(() => useModalContext());

function handleModalClosed() {
  if (route?.query?.freePlay) {
    settings.clearDemoPlayTimeout();
    settings.setDemoPlayTimeout();
  }
  settings.deactivateModal();
}
function disableAllActiveStates() {
  Object.entries(activeStates).forEach(([key, value]) => {
    if (value) activeStates[key] = false;
  });
}

const enableNotificationPanel = computed(
  () =>
    auth.loggedIn &&
    !!window &&
    !!window?.indexedDB &&
    $enabled('website.notificationpanel'),
);

const overlay = computed(
  () => activeStates.providers || activeStates.topNavigation,
);

const queriesToNotShowToasts = Object.keys(actionHandlersConfig).filter(
  (k) => k !== 'toast',
);

const showToast = computed(
  () =>
    !!$toastService.nextToast.value &&
    $enabled('website.toastmessages') &&
    !Object.keys(route.query).some((k) => queriesToNotShowToasts.includes(k)) &&
    !auth.accountModal &&
    !settings.modalActive &&
    !route.params?.game &&
    !overlay.value,
);

const nextToast = computed(() => $toastService.nextToast.value);

const hamburgerMenuState = reactive({
  active: false,
  drawerTransition: null,
});

provide<IHamburgerMenuState>('hamburgerMenuState', hamburgerMenuState);

useWindowFunctions();

watchEffect(() => {
  if (ctrl_k.value) settings.activateModal('search');
});
watchEffect(() => {
  if (hamburgerMenuState.active) disableAllActiveStates();
});
watchEffect(() => {
  if (activeStates.providers) {
    activeStates.topNavigation = false;
  } else if (activeStates.topNavigation) {
    activeStates.providers = false;
  }
});
watchEffect(() => {
  if (!!auth.currentAccessToken && !auth.isLoggedIn && !settings.getModalActive)
    auth.logout({ redirect: true, path: '/' });
});
const fixedJackpotsEnabled = computed(() =>
  $enabled('jackpots.enablefixedjackpot'),
);
watch(
  () => fixedJackpotsEnabled.value,
  async (value) => {
    if (value) {
      await $jackpotService.fetchFixedJackpotEvents();
    }
  },
  { once: true },
);
</script>
<template>
  <div>
    <LinearGradients />
    <div
      class="default-layout relative w-full"
      :class="overlay ? 'p-overflow-hidden max-h-screen' : 'min-h-screen'"
    >
      <LazyDesignSystemModal
        v-model:controller="settings.modalActive"
        v-bind="{
          ...(!!modalContent?.modalProps && modalContent.modalProps),
          ...(!!modalContent?.modalProps?.title && {
            title: modalContent?.modalProps?.forceUntranslatedTitle
              ? modalContent?.modalProps?.title
              : $t(
                  `${modalContent?.modalProps?.title}-${settings.getRegionCode}`,
                  modalContent?.modalProps?.title,
                ),
          }),
          ...(!!modalContent?.modalProps?.displayBackground &&
          !auth.getAccountModalPage
            ? {
                displayBackground: false,
              }
            : {
                displayBackground: modalContent?.modalProps?.displayBackground,
              }),
        }"
        @close="handleModalClosed()"
      >
        <template #modal-content>
          <component
            :is="modalContent?.component()"
            :key="modalContent?.modalProps?.title"
            v-bind="{
              ...(!!modalContent?.contentProps && modalContent.contentProps),
            }"
          />
        </template>
      </LazyDesignSystemModal>
      <LazyUserAccountModal
        v-model:controller="auth.displayAccountModal"
        @close="auth.toggleAccountModal(false)"
      />
      <LazyMarketingToast
        v-if="showToast"
        :key="route.path"
        :toast="nextToast"
      />
      <DesignSystemToastMessages
        :severity="settings.getSiteNotice.severity"
        :heading="settings.getSiteNotice.heading"
        :content="settings.getSiteNotice?.content"
        :position="settings.getSiteNotice?.position"
        :timeout="settings.getSiteNotice.timeout"
        :enabled="settings.getSiteNotice.enabled"
      />

      <NavigationBar v-show="!inGameMobile" />
      <div
        v-show="overlay"
        class="jpc-overlay"
        @click="disableAllActiveStates()"
      ></div>
      <UserInterfaceErrorNotice v-show="offline" overlay>
        <template #icon>
          <DesignSystemIconsOfflineIcon class="!w-20 md:!w-32" />
        </template>
        <template #title>
          {{ $t('network-error-title') }}
        </template>
        <template #message>
          {{ $t('network-error-message') }}
        </template>
      </UserInterfaceErrorNotice>
      <main
        v-show="!offline"
        :key="String(inGameMobile)"
        class="body-wrap pb-6 bg-base-secondary min-h-[40vh]"
      >
        <ErrorBoundary component-boundary="Main Slot" file="default.vue">
          <slot />
        </ErrorBoundary>
        <LazyUserInterfaceCookieBar v-if="$enabled('website.cookiebar')" />
      </main>

      <LazyUserInterfaceSiteFooter v-show="!route?.query?.id" />
      <LazyUserInterfaceMobileFooter v-show="!inGameMobile" />
      <LazyMarketingNotificationsDrawer v-if="enableNotificationPanel" />
      <LazyToolsDebugToolbar v-if="runtimeConfig?.public.enableDebugTools" />
    </div>
    <LazyMarketingPromotionsScriptHolster />
  </div>
</template>
